import { AdditionalInfo, ApiResponse, FormData, Stages } from '@/api/types';
import { API_API_URL, API_URL, PRODUCT } from '@/consts';
import { ADDON_LABELS, EcommerceFormValues } from '@/features/ecommerce/schema';

import { useMutation } from '@tanstack/react-query';
import { serialize } from 'object-to-formdata';
import { useParams } from 'react-router-dom';

export interface EcommerceLeadPayload {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  lineTypes: string[];
  plotType: string;
  coOwnersCount: string;
}

export const useEcommerceTransmissionCreateMutation = () =>
  useMutation({
    mutationFn: async (
      values: EcommerceLeadPayload
    ): Promise<{ id: string }> => {
      const requestParams = Object.fromEntries(
        new URLSearchParams(window.location.search)
      );
      const formData = serialize({
        first_name: values.firstName,
        sent_from_url: window.location.href,
        page: window.location.pathname,
        ...(Object.keys(requestParams).length > 0 && {
          request_params: requestParams,
        }),
        email: values.email,
        phone: values.phone,
        agreements: [
          'Akceptuję politykę prywatności oraz regulamin świadczenia usług przez Helpfind sp. z o.o.',
        ],
        productId: PRODUCT.TRANSMISSION,
        additional_fields: {
          last_name: values.lastName,
          line_types: values.lineTypes,
          plot_type: values.plotType,
          co_owners_count: values.coOwnersCount,
          do_not_send_messages: 1,
        },
      });

      const response = await fetch(`${API_API_URL}/api/hf/store`, {
        method: 'POST',
        body: formData,
        headers: { accept: 'application/json' },
      });

      if (!response.ok) throw new Error('Failed to create lead');

      const res = (await response.json()) as ApiResponse;
      // `data.hash` is not `{lead_id}`-shaped and is rejected by the
      // self-service API — `data.e_commerce` carries the correctly
      // formatted link for eligible products (e.g. transmission).
      const id = res.data.e_commerce?.split('/').pop() ?? '';
      return { id };
    },
  });

// --- Post-lead checkout flow: dedicated e-commerce hash endpoints ---
// GET/POST /api/self-service/e-commerce/{hash} and .../complete-stage.
// These are intentionally separate from the generic /api/self-service/{hash}
// endpoints used by the other products (household, vehicle-damage).

const ECOMMERCE_ROUTE_PREFIX = '/ecommerce/transmission/';

export const isEcommerceTransmissionPath = (pathname: string): boolean =>
  pathname.startsWith(ECOMMERCE_ROUTE_PREFIX);

// The self-service e-commerce endpoint returns 422 with `errors.lead_id`
// when the hash no longer maps to a lead (e.g. the lead was deleted).
export const isEcommerceLeadIdError = async (
  response: Response
): Promise<boolean> => {
  try {
    const body = await response.clone().json();
    return Array.isArray(body?.errors?.lead_id);
  } catch {
    return false;
  }
};

interface EcommerceAddon {
  value: string;
  label: string;
}

interface EcommerceStepper {
  step: number;
  innerStep: { [key: number]: number };
}

interface EcommercePayload {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: Record<string, any> | null;
  stepper?: EcommerceStepper;
}

interface EcommerceAccount {
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  email: string | null;
}

interface EcommerceShowApiResponse {
  id: number;
  account: EcommerceAccount;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  lead_product_info: Record<string, any>;
  payload: EcommercePayload | null;
  stage: string;
  step: string | null;
  payment_url: string | null;
}

const ecommerceUrl = (hash: string) =>
  `${API_URL}/api/self-service/e-commerce/${encodeURIComponent(hash)}`;

const mapEcommerceResponseToFormData = (
  resp: EcommerceShowApiResponse
): FormData => {
  const payload = resp.payload ?? {};
  const addons = (
    (payload.data?.addons as EcommerceAddon[] | undefined) ?? []
  ).map((addon) => addon.value);

  return {
    id: resp.id,
    is_blocked_for_client: false,
    is_email_empty: !resp.account?.email,
    product: {
      id: PRODUCT.TRANSMISSION,
      additional_info: {} as AdditionalInfo,
    },
    stage: resp.stage as Stages,
    step: null,
    offer: {} as FormData['offer'],
    user: {
      name: '',
      phone: resp.account.phone ?? '',
      email: resp.account.email ?? '',
      first_name: resp.account.first_name ?? '',
      last_name: resp.account.last_name,
      postal_code: '',
    },
    payload: {
      data: {
        ...resp.account,
        ...resp.lead_product_info,
        ...payload.data,
        document_type: payload.data?.document_type ?? '',
        addons,
      },
      stepper: payload.stepper ?? { step: 0, innerStep: {} },
    },
    consultant_completed_at: null,
    documents: [],
    send_for_signature: '',
    complementary_default_values: {},
    payment_url: resp.payment_url ?? null,
  };
};

export const fetchEcommerceFormData = async (
  hash: string
): Promise<FormData> => {
  const response = await fetch(ecommerceUrl(hash), {
    headers: { accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Response(await response.text(), { status: response.status });
  }

  const data = (await response.json()) as EcommerceShowApiResponse;
  return mapEcommerceResponseToFormData(data);
};

const mapFormValuesToEcommercePayload = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  values: Record<string, any>,
  stepper: EcommerceStepper
): EcommercePayload => {
  const { document_type, addons, ...rest } = values as EcommerceFormValues;

  return {
    data: {
      ...rest,
      document_type: document_type || undefined,
      addons: (addons ?? []).map((value) => ({
        value,
        label: ADDON_LABELS[value],
      })),
    },
    stepper,
  };
};

export const postEcommerceFormData = async (
  hash: string,
  // form_data comes from formMutationDto(): { form_data: { data, stepper } }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  requestBody: Record<string, any>,
  currentStage: Stages | string | undefined
): Promise<Response> => {
  const formData = requestBody.form_data as {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: Record<string, any> | null;
    stepper: EcommerceStepper;
  };
  const payload = mapFormValuesToEcommercePayload(
    formData.data ?? {},
    formData.stepper
  );

  const response = await fetch(ecommerceUrl(hash), {
    method: 'POST',
    body: JSON.stringify({ payload }),
    headers: {
      'Content-Type': 'application/json',
      accept: 'application/json',
    },
  });

  // The update endpoint only returns a boolean success flag, not the
  // resource — synthesize a minimal FormData-shaped body so the generic
  // useFormMutation consumer (which only reads `.stage`) doesn't treat this
  // as a stage change and trigger a needless refetch.
  return new Response(JSON.stringify({ stage: currentStage }), {
    status: response.ok ? 200 : response.status,
  });
};

export interface EcommerceCompany {
  nip: string;
  company_name: string;
  street: string | null;
  building_number: string | null;
  apartment_number: string | null;
  postal_code: string | null;
  city: string | null;
}

export class EcommerceCompanyLookupError extends Error {
  constructor(public readonly status: number) {
    super(`Company lookup failed with status ${status}`);
  }

  get isNotFound() {
    return this.status === 404;
  }
}

export const fetchEcommerceCompany = async (
  hash: string,
  nip: string
): Promise<EcommerceCompany> => {
  const response = await fetch(
    `${ecommerceUrl(hash)}/company?${new URLSearchParams({ nip })}`,
    { headers: { accept: 'application/json' } }
  );

  if (!response.ok) throw new EcommerceCompanyLookupError(response.status);

  return response.json();
};

export const useEcommerceCompanyMutation = () => {
  const { id } = useParams();

  return useMutation({
    mutationFn: (nip: string) => fetchEcommerceCompany(id ?? '', nip),
  });
};

export const completeEcommerceStage = (hash: string, stage: string) =>
  fetch(`${ecommerceUrl(hash)}/complete-stage`, {
    method: 'POST',
    body: JSON.stringify({ stage }),
    headers: {
      'Content-Type': 'application/json',
      accept: 'application/json',
    },
  });
